import { supabase } from "@/integrations/supabase/client";
import { uploadDeadlineMs } from "@/lib/executorUpload";

export { uploadDeadlineMs };

/**
 * uploadFile — util ÚNICO de upload para o Storage, par do downloadFile.ts.
 *
 * Nasce compartilhado pelo mesmo motivo que o downloadFile: hoje existem
 * quatro `.upload()` espalhados (exerciseVideoAdmin ×2, generateAvatarSnapshot,
 * BabyStimulation) e o último deles — `await supabase.storage.from('pictures')
 * .upload(filePath, file, { upsert: true })` — não olha o retorno. Um upload
 * que falha em silêncio vira URL quebrada depois, longe da causa.
 *
 * Regra da casa aqui: TODO caminho de erro loga antes de propagar (seção 11 do
 * CLAUDE.md). Nada de try/catch mudo.
 *
 * ── SOBRE CANCELAR NO MEIO ──────────────────────────────────────────────────
 * `FileOptions` do @supabase/storage-js NÃO tem `signal` (conferido na 2.99.3):
 * um upload em curso não é abortável pelo SDK. Então cancelamento aqui é
 * COMPENSATÓRIO, não preventivo — os bytes sobem de qualquer forma, e o que
 * fazemos é apagar o objeto assim que ele aterrissa. A UI responde na hora (o
 * chip some), a limpeza acontece atrás. Fingir que dá para abortar deixaria
 * lixo no bucket toda vez que alguém desistisse.
 */

const LOG = "[uploadFile]";

export interface UploadToBucketOpts {
  bucket: string;
  /** Caminho completo no bucket, incluindo a pasta raiz do dono. */
  path: string;
  file: File | Blob;
  contentType?: string;
  /** Prazo em ms. Default: proporcional ao tamanho (uploadDeadlineMs). */
  deadlineMs?: number;
  /**
   * Cancelamento COMPENSATÓRIO: quando já abortado no fim do upload, o objeto
   * recém-criado é removido e a promessa rejeita com `UploadAbortedError`.
   * Não interrompe a transferência — ver o cabeçalho.
   */
  signal?: AbortSignal;
}

/** Rejeição por cancelamento do usuário — distinta de falha real de rede. */
export class UploadAbortedError extends Error {
  constructor() {
    super("upload aborted by caller");
    this.name = "UploadAbortedError";
  }
}

/** Estourou o prazo. Distinta de erro de rede: o objeto AINDA pode chegar. */
export class UploadTimeoutError extends Error {
  constructor(ms: number) {
    super("upload exceeded " + String(ms) + "ms");
    this.name = "UploadTimeoutError";
  }
}


/**
 * Sobe um arquivo e devolve o caminho gravado.
 *
 * `upsert: false` de propósito: no fluxo do Executor o caminho carrega um uuid
 * novo a cada anexo, então colisão significa bug (ou reenvio duplicado), e
 * sobrescrever em silêncio esconderia os dois.
 */
export async function uploadToBucket(opts: UploadToBucketOpts): Promise<string> {
  const { bucket, path, file, contentType, signal } = opts;

  if (signal?.aborted) throw new UploadAbortedError();

  const deadlineMs = opts.deadlineMs ?? uploadDeadlineMs(file.size);

  const inFlight = supabase.storage.from(bucket).upload(path, file, {
    upsert: false,
    contentType: contentType || (file instanceof File ? file.type : undefined) || undefined,
  });

  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new UploadTimeoutError(deadlineMs)), deadlineMs);
  });

  let result: Awaited<typeof inFlight>;
  try {
    result = await Promise.race([inFlight, deadline]);
  } catch (err) {
    if (err instanceof UploadTimeoutError) {
      console.error(
        LOG + " upload timed out after " + String(deadlineMs) + "ms bucket=" + bucket +
          " path=" + path + " bytes=" + String(file.size),
      );
      // Perder a corrida NÃO cancela a transferência — o SDK não expõe como.
      // Então o objeto pode aterrissar depois, sozinho, sem linha nenhuma
      // apontando para ele. Limpeza compensatória: quando (e se) assentar,
      // recolhe. O varredor de órfãos é a rede embaixo; isto é o plano.
      void inFlight
        .then((late) => {
          if (!late.error) void removeFromBucket(bucket, [path]);
        })
        .catch(() => {/* já falhou por conta própria: nada a recolher */});
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }

  if (result.error) {
    console.error(LOG + " upload failed bucket=" + bucket + " path=" + path, result.error);
    throw result.error;
  }

  // Aterrissou depois de o usuário desistir → recolhe o que subiu.
  if (signal?.aborted) {
    await removeFromBucket(bucket, [path]);
    throw new UploadAbortedError();
  }

  return path;
}

/**
 * Apaga objetos do bucket. Devolve `true` quando o Storage confirmou.
 *
 * NÃO lança: quem chama isto está quase sempre num caminho de limpeza (falha,
 * cancelamento, delete da Biblioteca) e derrubar a limpeza por causa da
 * limpeza troca um objeto órfão por um erro na cara do usuário. O varredor de
 * órfãos (`executor-sweep-orphans`, cron horário) é a rede embaixo — mas ele é
 * a rede, não o plano: o caminho normal apaga aqui e agora.
 */
export async function removeFromBucket(bucket: string, paths: string[]): Promise<boolean> {
  if (paths.length === 0) return true;
  try {
    const { error } = await supabase.storage.from(bucket).remove(paths);
    if (error) {
      console.error(LOG + " remove failed bucket=" + bucket + " paths=" + paths.join(","), error);
      return false;
    }
    return true;
  } catch (err) {
    console.error(LOG + " remove threw bucket=" + bucket, err);
    return false;
  }
}
